/**
 * SplitMatchModal — manueller Split einer Gutschrift auf mehrere Rechnungen
 * (architecture-bank-reconciliation.md §8.5). Der Nutzer wählt Rechnungen und
 * Teilbeträge; das UI erzwingt die Invariante `Σ Teilbeträge == tx.amountCents`
 * (Integer-Cents) bevor „Bestätigen" aktiv wird — dieselbe harte Invariante, die
 * `confirmIncomingMatch` server-nah nochmals prüft (§9.3).
 *
 * @module SplitMatchModal
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { eurosToCents, centsToEuros } from '../../utils/moneyUtils';
import { BankTransaction } from '../../types/bankTypes';
import { MatchableInvoice } from '../../utils/bankMatching';

export interface SplitMatchModalProps {
  tx: BankTransaction;
  invoices: MatchableInvoice[];
  onCancel: () => void;
  onConfirm: (parts: Array<{ invoiceId: string; amountCents: number }>) => void;
}

interface Allocation {
  selected: boolean;
  /** Euro string as typed by the user; converted to cents on confirm. */
  amountEuros: string;
}

const SplitMatchModal: React.FC<SplitMatchModalProps> = ({ tx, invoices, onCancel, onConfirm }) => {
  const { t, i18n } = useTranslation('bank');
  const locale = i18n.language || 'de-DE';

  // Only open invoices are meaningful split targets.
  const openInvoices = useMemo(
    () => invoices.filter((inv) => inv.openAmountCents > 0),
    [invoices],
  );

  const [alloc, setAlloc] = useState<Record<string, Allocation>>({});

  const formatAmount = (cents: number) =>
    centsToEuros(cents).toLocaleString(locale, { style: 'currency', currency: 'EUR' });

  const parts = useMemo(() => {
    const out: Array<{ invoiceId: string; amountCents: number }> = [];
    for (const inv of openInvoices) {
      const a = alloc[inv.invoiceId];
      if (!a?.selected) continue;
      const euros = parseFloat((a.amountEuros || '').replace(',', '.'));
      if (!Number.isFinite(euros) || euros <= 0) continue;
      out.push({ invoiceId: inv.invoiceId, amountCents: eurosToCents(euros) });
    }
    return out;
  }, [alloc, openInvoices]);

  const sum = parts.reduce((acc, p) => acc + p.amountCents, 0);
  const target = tx.amountCents;
  const balanced = parts.length > 0 && sum === target;

  const toggle = (inv: MatchableInvoice) => {
    setAlloc((prev) => {
      const cur = prev[inv.invoiceId];
      if (cur?.selected) {
        return { ...prev, [inv.invoiceId]: { selected: false, amountEuros: '' } };
      }
      return {
        ...prev,
        [inv.invoiceId]: {
          selected: true,
          amountEuros: centsToEuros(inv.openAmountCents).toFixed(2),
        },
      };
    });
  };

  const setAmount = (invoiceId: string, value: string) => {
    setAlloc((prev) => ({
      ...prev,
      [invoiceId]: { selected: true, amountEuros: value },
    }));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-surface border border-hairline rounded-2xl w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto">
        <div>
          <h3 className="text-primary font-semibold">{t('split.title', 'Zahlung splitten')}</h3>
          <p className="text-sm text-muted mt-1">
            {t('split.subtitle', 'Betrag {{amount}} auf mehrere Rechnungen verteilen', {
              amount: formatAmount(target),
            })}
          </p>
        </div>

        <div className="space-y-2">
          {openInvoices.length === 0 && (
            <p className="text-sm text-faint">{t('split.noInvoices', 'Keine offenen Rechnungen.')}</p>
          )}
          {openInvoices.map((inv) => {
            const a = alloc[inv.invoiceId];
            return (
              <div key={inv.invoiceId} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!a?.selected}
                  onChange={() => toggle(inv)}
                  aria-label={inv.invoiceNumber}
                />
                <span className="flex-1 min-w-0 truncate text-secondary">
                  {inv.invoiceNumber}
                  <span className="text-faint ml-1">({formatAmount(inv.openAmountCents)})</span>
                </span>
                {a?.selected && (
                  <input
                    type="text"
                    inputMode="decimal"
                    value={a.amountEuros}
                    onChange={(e) => setAmount(inv.invoiceId, e.target.value)}
                    className="w-24 bg-surface-raised border border-hairline rounded px-2 py-1 text-right text-primary text-sm focus:outline-none focus:border-indigo-500"
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between text-sm border-t border-hairline pt-3">
          <span className="text-muted">{t('split.sum', 'Summe')}</span>
          <span className={balanced ? 'text-[var(--tone-success-fg)]' : 'text-[var(--tone-warning-fg)]'}>
            {formatAmount(sum)} / {formatAmount(target)}
          </span>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-muted hover:text-primary"
          >
            {t('split.cancel', 'Abbrechen')}
          </button>
          <button
            type="button"
            disabled={!balanced}
            onClick={() => onConfirm(parts)}
            className="px-5 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 disabled:cursor-not-allowed text-white rounded-lg"
          >
            {t('split.confirm', 'Bestätigen')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SplitMatchModal;
